// Pure validation for the Add Channel modal (Channels page). Kept separate from
// the component so it can be unit-tested and mirrors the backend rules
// (ChannelCreateSerializer + BrokerChannelViewSet).

export interface AddChannelSubForm {
  name?: string;
  code?: string;
  email?: string;
  phone?: string;
  commission_percentage?: string;
}

export interface AddChannelForm {
  name?: string;
  code?: string;
  /** Either a built-in Channel.TYPE_CHOICES value ('POSP', 'ADVERTISER', …) or
   *  a custom type encoded as `custom:<uuid>` (board #252). */
  channel_type?: string;
  /** Set alongside a `custom:<uuid>` channel_type: the built-in that custom
   *  type declares it behaves as (its server-side `base_type`). Every rule
   *  below keys off the RESOLVED base type, never the raw select value. */
  custom_base_type?: string;
  email?: string;
  phone?: string;
  /** Login password for a POSP channel's agent (create only). Prefilled with
   *  the default "Test@1234"; the agent must change it on first login. */
  password?: string;
  /** Board #267: the id of an EXISTING agent to link, from the "Linked Agent /
   *  POSP" select. Only offered when the resolved base type is POSP. When set,
   *  the server provisions no login (the agent already has one), so the
   *  "email or phone is required" rule below does not apply. */
  posp?: string;
  commission_percentage?: string;
  subchannels?: AddChannelSubForm[];
}

export interface AddChannelSubErrors {
  name?: string;
  code?: string;
  email?: string;
  commission_percentage?: string;
}

export interface AddChannelErrors {
  name?: string;
  code?: string;
  channel_type?: string;
  email?: string;
  posp?: string;
  commission_percentage?: string;
  subchannels?: Record<number, AddChannelSubErrors>;
}

/** Board #252: how a custom channel type is encoded in the single <select>.
 *  `Select` renders a flat option list (no optgroup), so custom types share the
 *  control with the built-ins and are told apart by this prefix. */
export const CUSTOM_TYPE_PREFIX = 'custom:';

export function isCustomChannelType(value?: string): boolean {
  return Boolean(value?.startsWith(CUSTOM_TYPE_PREFIX));
}

/** The ChannelType id inside a `custom:<uuid>` select value, else undefined. */
export function customChannelTypeId(value?: string): string | undefined {
  return isCustomChannelType(value) ? value!.slice(CUSTOM_TYPE_PREFIX.length) : undefined;
}

/** The built-in type whose BEHAVIOUR applies to this form.
 *
 *  This is the whole point of the design: a custom type is not a new enum
 *  member, it declares a base. Validating against the raw select value would
 *  mean a POSP-based custom type skipped the "needs an email or phone" rule --
 *  the server would then create a channel with no agent and no login, and the
 *  UI would report success. That is this codebase's signature bug, so the
 *  resolution happens here, once, where every rule can share it.
 */
export function resolveBaseType(form: AddChannelForm): string | undefined {
  if (!form.channel_type) return undefined;
  if (isCustomChannelType(form.channel_type)) return form.custom_base_type;
  return form.channel_type;
}

/** `POSP.posp_code` is varchar(20). A code that becomes an agent's code must
 *  fit -- the server refuses a longer one (15 Sep QA batch, B8). */
export const POSP_CODE_MAX_LENGTH = 20;

/** B8: the agent code typed into the Add POSP form (POSPs tab). Required --
 *  the server no longer invents a POSP... code when one is given, and the form
 *  always gives one. Uniqueness within the broker is the server's call. */
export function validatePospCode(code?: string): string | undefined {
  const value = code?.trim() ?? '';
  if (!value) return 'Code is required';
  if (value.length > POSP_CODE_MAX_LENGTH) {
    return `Code can be at most ${POSP_CODE_MAX_LENGTH} characters`;
  }
  return undefined;
}

function agentCodeLengthError(code?: string): string | undefined {
  const value = code?.trim() ?? '';
  return value.length > POSP_CODE_MAX_LENGTH
    ? `An agent's code can be at most ${POSP_CODE_MAX_LENGTH} characters`
    : undefined;
}

function pctError(value?: string): string | undefined {
  if (!value) return undefined;
  const n = Number(value);
  if (Number.isNaN(n) || n < 0 || n > 100) return 'Must be between 0 and 100';
  return undefined;
}

export function validateAddChannel(form: AddChannelForm): AddChannelErrors {
  const e: AddChannelErrors = {};
  const baseType = resolveBaseType(form);

  if (!form.name?.trim()) e.name = 'Name is required';
  if (!form.code?.trim()) e.code = 'Code is required';
  if (!form.channel_type) e.channel_type = 'Channel Type is required';
  else if (!baseType) {
    // A custom option whose base_type never arrived. Submitting would let the
    // server pick the behaviour while the form validated against nothing.
    e.channel_type = 'This channel type is missing its base type. Reload and try again.';
  }

  const pctErr = pctError(form.commission_percentage);
  if (pctErr) e.commission_percentage = pctErr;

  // A POSP channel provisions a login, so it needs an email or phone. Keyed on
  // the RESOLVED base type so a POSP-based custom type is held to the same rule.
  //
  // Board #267: unless an EXISTING agent is linked. That agent already has a
  // login and the server creates no User, so demanding contact details here
  // would block the only flow that fixes an agent stranded without a channel.
  if (
    baseType === 'POSP' &&
    !form.posp?.trim() &&
    !form.email?.trim() &&
    !form.phone?.trim()
  ) {
    e.email = 'Email or phone is required for a POSP';
  }

  // A POSP channel with contact details provisions an agent whose code is this
  // code, so it has to fit the agent code column.
  if (
    !e.code &&
    baseType === 'POSP' &&
    !form.posp?.trim() &&
    (form.email?.trim() || form.phone?.trim())
  ) {
    const codeErr = agentCodeLengthError(form.code);
    if (codeErr) e.code = codeErr;
  }

  // Board #267: `posp` is meaningless on any other base type and the server
  // 400s it, so say so here rather than round-tripping.
  if (form.posp?.trim() && baseType && baseType !== 'POSP') {
    e.posp = 'Only a POSP channel can be linked to an agent.';
  }

  // Subchannels only apply to an Advertiser; each must have a name and a contact.
  const subErrors: Record<number, AddChannelSubErrors> = {};
  if (baseType === 'ADVERTISER') {
    (form.subchannels ?? []).forEach((s, i) => {
      const se: AddChannelSubErrors = {};
      if (!s.name?.trim()) se.name = 'Name is required';
      if (!s.email?.trim() && !s.phone?.trim()) se.email = 'Email or phone is required';
      const codeErr = agentCodeLengthError(s.code);
      if (codeErr) se.code = codeErr;
      const sp = pctError(s.commission_percentage);
      if (sp) se.commission_percentage = sp;
      if (Object.keys(se).length) subErrors[i] = se;
    });
  }
  if (Object.keys(subErrors).length) e.subchannels = subErrors;

  return e;
}

export function hasErrors(e: AddChannelErrors): boolean {
  const { subchannels, ...top } = e;
  if (Object.values(top).some(Boolean)) return true;
  return !!subchannels && Object.keys(subchannels).length > 0;
}
